import { createTRPCProxyClient, httpBatchLink, httpLink, loggerLink, splitLink } from "@trpc/client";
import { observable } from "@trpc/server/observable";
import { TypeCompiler } from "@sinclair/typebox/compiler";
import { getHTTPStatusCodeFromError } from "@trpc/server/http";
//#endregion
//#region src/client.ts
const createErrorHandlerLink = (errorMapper) => {
	return () => {
		return ({ next, op }) => {
			return observable((observer) => {
				return next(op).subscribe({
					next(value) {
						observer.next(value);
					},
					error(err) {
						const mapped = errorMapper?.(err) ?? err;
						observer.error(mapped);
					},
					complete() {
						observer.complete();
					}
				});
			});
		};
	};
};
/**
* Create a tRPC client.
* @see https://trpc.io/docs/vanilla
*/
function createTRPCClient(opts) {
	const links = [];
	if (typeof window === "undefined" && !opts.fetch) throw new Error("Calling createTRPCClient() on the server requires passing a valid \"fetch\" argument");
	if (!opts.baseUrl) throw new Error("Calling createTRPCClient() requires \"baseUrl\" argument");
	const apiUrl = opts.baseUrl + (opts.url ?? "/api/trpc");
	const isBatch = opts.batch ?? true;
	const fetch = opts.fetch ?? window.fetch;
	links.push(createErrorHandlerLink(opts.errorMapper));
	if (opts.log) links.push(loggerLink());
	const linkOptions = {
		url: apiUrl,
		fetch,
		headers: getHeaders(opts)
	};
	if (isBatch) links.push(splitLink({
		condition: (op) => op.context.skipBatch === true,
		true: httpLink(linkOptions),
		false: httpBatchLink(linkOptions)
	}));
	else links.push(httpLink(linkOptions));
	return createTRPCProxyClient({
		links,
		transformer: opts.transformer
	});
}
function getHeaders(options) {
	return async (opts) => {
		const headers = { ...typeof options.headers === "function" ? await options.headers(opts) : options.headers };
		let streamId = null;
		if ("op" in opts) streamId = opts.op.context.streamId;
		else if ("opList" in opts) streamId = opts.opList[0].context.streamId;
		if (streamId) headers["x-stream-id"] = streamId;
		if (options.authProvider) switch (options.authProvider.provider) {
			case "firebase": {
				const { firebaseOAuth, serviceUid } = options.authProvider;
				await firebaseOAuth.initCustomTokenAuth(serviceUid);
				headers.Authorization = `Bearer ${await firebaseOAuth.getJWTToken()}`;
				break;
			}
			default: throw new Error(`Unknown auth provider: ${options.authProvider.provider}`);
		}
		return headers;
	};
}
//#endregion
//#region src/typebox-adapter.ts
function wrapSchema(schema, references = []) {
	const compiledSchema = TypeCompiler.Compile(schema, references);
	return {
		_input: void 0,
		_output: void 0,
		parse: (value) => {
			if (compiledSchema.Check(value)) return value;
			const errors = Array.from(compiledSchema.Errors(value));
			const [{ path, message }] = errors;
			throw new AggregateError(errors, `${message} for ${path}`);
		}
	};
}
//#endregion
//#region src/helpers.ts
const errorFormatter = (opts) => {
	const { shape, error } = opts;
	const cause = error.cause;
	const data = {
		...shape.data,
		code: cause && "code" in cause ? cause.code : shape.data.code,
		httpStatus: cause ? getHTTPStatusCodeFromError(cause) : shape.data.httpStatus
	};
	if (cause instanceof AggregateError) data.errors = cause.errors;
	return {
		...shape,
		data
	};
};
//#endregion
export { createErrorHandlerLink, createTRPCClient, errorFormatter, wrapSchema };

//# sourceMappingURL=index.mjs.map